import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Bewerber, BewerberStatus, einstellenPhase } from '../../types/bewerber';
import { BewerberActionDropdown } from './actions';
import { ChecklistBadge } from './ChecklistBadge';
import { UserAvatar } from '../UserAvatar';
import { formatOrtStellen, formatOrtStellenTooltip } from '../../utils/formatOrtStellen';
import { useDetailNavigation } from '../../hooks/useDetailNavigation';

interface KanbanCardProps {
  bewerber: Bewerber;
  isDragging?: boolean;
}

export function KanbanCard({ bewerber, isDragging }: KanbanCardProps) {
  const detailProps = useDetailNavigation();
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
  } = useSortable({
    id: bewerber.id,
    data: {
      type: 'bewerber',
      bewerber,
    },
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      {...(isDragging ? {} : detailProps(`/bewerber/${bewerber.id}`))}
      className={`
        bg-white rounded-lg shadow-sm p-3 mb-2
        cursor-grab active:cursor-grabbing
        hover:shadow-md transition-all
        ${isDragging ? 'opacity-50 shadow-lg ring-2 ring-primary-500' : ''}
        ${bewerber.isMarked ? 'ring-2 ring-amber-400 border-amber-300 border' : 'border border-gray-200 hover:border-gray-300'}
      `}
    >
      {/* Header with initials and action menu */}
      <div className="flex items-start mb-2">
        <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center mr-2 flex-shrink-0">
          <span className="text-primary-600 font-medium text-xs">
            {bewerber.firstName[0]}{bewerber.lastName[0]}
          </span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1">
            <p className="text-sm font-medium text-gray-900 truncate">
              {bewerber.firstName} {bewerber.lastName}
            </p>
            {/* Visual markers */}
            {bewerber.isReopened && (
              <span
                className="inline-flex items-center px-1 py-0.5 rounded text-xs bg-blue-100 text-blue-700"
                title="Reaktiviert"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              </span>
            )}
            {bewerber.hasSecondApplication && (
              <span
                className="inline-flex items-center px-1 py-0.5 rounded text-xs bg-amber-100 text-amber-700"
                title="Zweite Bewerbung"
              >
                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
              </span>
            )}
            {bewerber.isMarked && (
              <span
                className="inline-flex items-center px-1 py-0.5 rounded text-xs bg-amber-100 text-amber-700"
                title="Markiert"
              >
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M4 4v17l6-3 6 3V4H4zm0-2h12a2 2 0 012 2v17.586a1 1 0 01-1.707.707L10 18.586l-6.293 3.707A1 1 0 012 21.586V4a2 2 0 012-2z" />
                </svg>
              </span>
            )}
          </div>
        </div>
        {/* Action dropdown - prevent drag and click propagation */}
        <div
          onClick={(e) => e.stopPropagation()}
          onAuxClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          className="flex-shrink-0 -mr-1 -mt-1"
        >
          <BewerberActionDropdown bewerber={bewerber} size="sm" align="right" />
        </div>
      </div>

      {/* Details */}
      <div className="space-y-1 text-xs text-gray-500">
        {/* Fachkraft status */}
        {bewerber.isFachkraft !== null && (
          <div className="flex items-center">
            <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
              bewerber.isFachkraft
                ? 'bg-green-100 text-green-700'
                : 'bg-orange-100 text-orange-700'
            }`}>
              {bewerber.isFachkraft ? 'Fachkraft' : 'Nicht-Fachkraft'}
            </span>
          </div>
        )}
        {/* City of residence - home icon */}
        {bewerber.city && (
          <div className="flex items-center">
            <svg className="h-3 w-3 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span className="truncate">{bewerber.city.name}</span>
          </div>
        )}
        {/* Cities applied to (workplaces) - pin icon for destination */}
        {bewerber.ortStellen && bewerber.ortStellen.length > 0 && (
          <div className="flex items-center" title={formatOrtStellenTooltip(bewerber.ortStellen)}>
            <svg className="h-3 w-3 mr-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span className="truncate">{formatOrtStellen(bewerber.ortStellen, { max: 2 })}</span>
          </div>
        )}
        {/* "Warten auf KT-Go" without a Bedarf is not waiting for an office —
            the assignment is what is missing. */}
        {bewerber.status === BewerberStatus.ONBOARDING &&
          einstellenPhase(bewerber) === 'KT_GO' &&
          !bewerber.zugeordneteBedarfe?.length && (
            <div className="text-gray-400 italic">Kein Bedarf zugeordnet</div>
          )}
      </div>

      {/* Footer with document count and, where a checklist is being worked, its state */}
      <div className="mt-2 pt-2 border-t border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-gray-400">
          <span className="flex items-center">
            <svg className="h-3 w-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span>{bewerber.dokumente?.length || 0} Dok.</span>
          </span>
          <ChecklistBadge bewerber={bewerber} />
        </div>
        <div className="flex items-center gap-2">
          {bewerber.assignedTo && (
            <UserAvatar user={bewerber.assignedTo} size="xs" />
          )}
          <div className="text-xs text-gray-400">
            {new Date(bewerber.updatedAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' })}
          </div>
        </div>
      </div>
    </div>
  );
}

// Overlay version for drag preview
export function KanbanCardOverlay({ bewerber }: { bewerber: Bewerber }) {
  return (
    <div className={`bg-white rounded-lg shadow-xl border-2 p-3 w-56 rotate-3 ${bewerber.isMarked ? 'border-amber-400 ring-2 ring-amber-300' : 'border-primary-500'}`}>
      <div className="flex items-center mb-2">
        <div className="h-8 w-8 rounded-full bg-primary-100 flex items-center justify-center mr-2">
          <span className="text-primary-600 font-medium text-xs">
            {bewerber.firstName[0]}{bewerber.lastName[0]}
          </span>
        </div>
        <p className="text-sm font-medium text-gray-900 truncate">
          {bewerber.firstName} {bewerber.lastName}
        </p>
      </div>
      <div className="space-y-1 text-xs text-gray-500">
        {bewerber.isFachkraft !== null && (
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${
            bewerber.isFachkraft ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'
          }`}>
            {bewerber.isFachkraft ? 'Fachkraft' : 'Nicht-Fachkraft'}
          </span>
        )}
        {bewerber.city && <p>{bewerber.city.name}</p>}
        {bewerber.ortStellen && bewerber.ortStellen.length > 0 && (
          <p>{formatOrtStellen(bewerber.ortStellen, { max: 2 })}</p>
        )}
      </div>
    </div>
  );
}
